'use client'

import * as React from 'react'
import Link from 'next/link'
import { Trash2, Loader2, MapPin, Heart, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/components/auth'
import { useUserStore } from '@/lib/store'
import { getSavedListingIds, unsaveListing } from '@/lib/firebase'
import { api } from '@/lib/api'
import { formatPrice, proxyImageUrl } from '@/lib/utils'
import { cn } from '@/lib/utils'
import { ListingImagePlaceholder } from './listing-image-placeholder'
import type { Listing } from '@/lib/types'

function isListingActive(listing: Listing | null): listing is Listing {
  if (!listing) return false
  return listing.is_active === true || listing.is_active === 1
}

interface SavedListingsPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SavedListingsPanel({ open, onOpenChange }: SavedListingsPanelProps) {
  const { user } = useAuth()
  const { savedListingIds, setSavedListingIds, removeSavedListing } = useUserStore()
  
  const [listings, setListings] = React.useState<Listing[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [removingId, setRemovingId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  
  // Fetch saved listings when panel opens
  React.useEffect(() => {
    if (open && user) {
      fetchSavedListings()
    }
  }, [open, user])
  
  const fetchSavedListings = async () => {
    if (!user) return
    
    setIsLoading(true)
    setError(null)
    
    try {
      // First, get saved listing IDs from Firebase
      const ids = await getSavedListingIds(user.uid)
      setSavedListingIds(ids)
      
      if (ids.length === 0) {
        setListings([])
        setIsLoading(false)
        return
      }
      
      // Fetch listing details for each ID
      const listingPromises = ids.map(async (id) => {
        try {
          return await api.getListing(id)
        } catch (err) {
          console.error(`Error fetching listing ${id}:`, err)
          return null
        }
      })
      
      const results = await Promise.all(listingPromises)
      const validListings = results.filter(isListingActive)
      setListings(validListings)
    } catch (err) {
      console.error('Error fetching saved listings:', err)
      setError('Failed to load liked listings')
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleRemove = async (listingId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    
    if (!user) return
    
    setRemovingId(listingId)
    
    try {
      await unsaveListing(user.uid, listingId)
      removeSavedListing(listingId)
      setListings(prev => prev.filter(l => l.id !== listingId))
    } catch (err) {
      console.error('Error removing listing:', err)
      setError('Failed to remove listing')
    } finally {
      setRemovingId(null)
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Liked Listings</DialogTitle>
        
        {/* Hero Header */}
        <div className="bg-card pl-6 pr-14 pt-6 pb-5 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-rose-500/15 border-2 border-rose-500/30 flex items-center justify-center flex-shrink-0">
              <Heart className="h-5 w-5 text-rose-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground tracking-tight">Liked Listings</h2>
              <p className="text-sm text-muted-foreground mt-0.5">Your favorite apartments in one place.</p>
            </div>
          </div>
        </div>
        
        {/* Stats Bar */}
        {!isLoading && !error && listings.length > 0 && (
          <div className="flex items-center justify-center gap-2 px-6 py-3 bg-muted/30 border-b border-border/50">
            <div className="flex items-center gap-1.5 text-xs font-medium text-foreground bg-background rounded-full px-3 py-1.5 border border-border shadow-sm">
              <Heart className="h-3.5 w-3.5 text-rose-500" />
              <span className="font-bold">{listings.length}</span> liked
            </div>
          </div>
        )}
        
        {/* Content */}
        <div className="max-h-[55vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-primary mb-3" />
              <p className="text-sm text-muted-foreground">Loading your favorites...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16 px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
                <X className="h-8 w-8 text-destructive" />
              </div>
              <p className="text-destructive mb-4 font-medium">{error}</p>
              <Button variant="outline" size="lg" onClick={() => { setError(null); fetchSavedListings() }}>
                Try Again
              </Button>
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-14 px-8">
              <div className="flex items-center justify-center gap-2.5 mb-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-10 w-10 rounded-full border-2 border-dashed border-muted-foreground/15"
                  />
                ))}
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No liked listings yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Click the heart icon on listings to save them here for quick access.
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {listings.map((listing, index) => (
                <div
                  key={listing.id}
                  className="group relative rounded-xl border bg-card border-l-4 border-l-transparent hover:border-l-rose-400 transition-all hover:shadow-md overflow-hidden"
                  style={{
                    animationName: 'fadeInUp',
                    animationDuration: '0.3s',
                    animationTimingFunction: 'ease-out',
                    animationFillMode: 'forwards',
                    animationDelay: `${index * 60}ms`,
                  }}
                >
                  <Link
                    href={`/listings/${listing.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4"
                  >
                    {/* Thumbnail */}
                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-muted border border-border">
                      {listing.images[0]?.url ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={proxyImageUrl(listing.images[0].url)}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden') }}
                        />
                      ) : null}
                      <div className={cn('w-full h-full', listing.images[0]?.url && 'hidden')}>
                        <ListingImagePlaceholder variant="small" />
                      </div>
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm text-foreground line-clamp-1 group-hover:text-primary transition-colors mb-1">
                        {listing.title}
                      </h3>
                      
                      <p className="text-lg font-bold text-foreground">
                        {formatPrice(listing.price, listing.price_currency)}
                      </p>
                      
                      {/* Meta info */}
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {(listing.rooms || listing.area) && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {listing.rooms && <span>{listing.rooms} rooms</span>}
                            {listing.rooms && listing.area && <span>·</span>}
                            {listing.area && <span>{listing.area} m²</span>}
                          </div>
                        )}
                        {(listing.district || listing.city) && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate">
                              {[listing.district, listing.city].filter(Boolean).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                  
                  {/* Remove button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleRemove(listing.id, e)}
                    disabled={removingId === listing.id}
                    className={cn(
                      'absolute top-3 right-3 h-9 w-9 rounded-full transition-all',
                      'opacity-0 group-hover:opacity-100',
                      'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                    )}
                  >
                    {removingId === listing.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Inline keyframes style */}
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  )
}
